import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useCallback, useRef, useEffect } from 'react';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { leftSideTools } from '../interface/gameController';
var SelectionLayer = function (_a) {
    var onSelectionStart = _a.onSelectionStart;
    var _b = globalStore.useStoreState(function (store) { return store.playerStore; }), canvasWidth = _b.canvasWidth, canvasHeight = _b.canvasHeight;
    var _c = useContext(DataContext), tool = _c.tool, selectedTool = _c.selectedTool, setSelectedTool = _c.setSelectedTool, panningEnable = _c.panningEnable;
    var layerRef = useRef(null);
    // Active when selection tool is selected and panning is off
    var isActive = tool === leftSideTools.selection && !panningEnable;
    // Keep refs in sync so the native listener always has current values
    var isActiveRef = useRef(isActive);
    var selectedToolRef = useRef(selectedTool);
    var setSelectedToolRef = useRef(setSelectedTool);
    var canvasWidthRef = useRef(canvasWidth);
    var canvasHeightRef = useRef(canvasHeight);
    var onSelectionStartRef = useRef(onSelectionStart);
    useEffect(function () {
        isActiveRef.current = isActive;
        selectedToolRef.current = selectedTool;
        setSelectedToolRef.current = setSelectedTool;
        canvasWidthRef.current = canvasWidth;
        canvasHeightRef.current = canvasHeight;
        onSelectionStartRef.current = onSelectionStart;
    }, [isActive, selectedTool, setSelectedTool, canvasWidth, canvasHeight, onSelectionStart]);
    var handleMouseDown = useCallback(function (e) {
        if (!isActive)
            return;
        // If elements are selected, clear selection on click
        if (selectedTool.length > 0) {
            // setSelectedTool([]);
            return;
        }
        // No elements selected - start selection rectangle
        var target = e.currentTarget;
        var rect = target.getBoundingClientRect();
        var scaleX = canvasWidth / rect.width;
        var scaleY = canvasHeight / rect.height;
        var x = (e.clientX - rect.left) * scaleX;
        var y = (e.clientY - rect.top) * scaleY;
        onSelectionStart({
            x1: x,
            x2: null,
            y1: y,
            y2: null,
            points: [{ x: x, y: y }],
        });
    }, [isActive, selectedTool, setSelectedTool, canvasWidth, canvasHeight, onSelectionStart]);
    // Use native touchstart listener with { passive: false } so preventDefault() works in Safari
    // React's onTouchStart is passive in Safari, making preventDefault() silently fail
    useEffect(function () {
        var el = layerRef.current;
        if (!el)
            return;
        var handleTouchStart = function (e) {
            // Prevent Safari/iPad blue selection highlight
            e.preventDefault();
            if (!isActiveRef.current)
                return;
            if (selectedToolRef.current.length > 0) {
                // Match the mouse path (handleMouseDown), which deliberately does NOT
                // clear here. Clearing on touch dropped the selection — and when a
                // manipulative label (e.g. a number-line tick) was being edited, that
                // reset selectedTool, blurred the MathEditor and closed the virtual
                // keypad the instant the user tapped a fraction num/den placeholder.
                // Genuine tap-on-empty-canvas deselection is handled by WhiteBoard's
                // own mousedown/touchstart handler.
                // setSelectedToolRef.current([]);
                return;
            }
            var touch = e.touches[0];
            var rect = el.getBoundingClientRect();
            var scaleX = canvasWidthRef.current / rect.width;
            var scaleY = canvasHeightRef.current / rect.height;
            var x = (touch.clientX - rect.left) * scaleX;
            var y = (touch.clientY - rect.top) * scaleY;
            onSelectionStartRef.current({
                x1: x,
                x2: null,
                y1: y,
                y2: null,
                points: [{ x: x, y: y }],
            });
        };
        el.addEventListener('touchstart', handleTouchStart, { passive: false });
        return function () {
            el.removeEventListener('touchstart', handleTouchStart);
        };
    }, []);
    return (_jsx("div", { ref: layerRef, className: "selectionLayer", style: {
            position: 'absolute',
            width: canvasWidth,
            height: canvasHeight,
            top: 0,
            left: 0,
            // Only intercept taps when the selection tool is active AND nothing
            // is selected — the layer's sole job is starting a lasso on empty
            // canvas. Once something is selected (or a manipulative label is
            // being edited), stay pass-through so taps reach the selected item
            // (e.g. tapping a fraction num/den placeholder to move the caret)
            // instead of being swallowed here and closing the editor/keypad.
            pointerEvents: isActive && selectedTool.length === 0 ? 'auto' : 'none',
            zIndex: 0,
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            touchAction: 'none',
        }, onMouseDown: handleMouseDown }));
};
export default SelectionLayer;
